import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { translateBackendError } from '@/api/translateBackendError';
import { cn } from '@/lib/utils';
import { ASSISTANT_SCROLL_FOLLOW_THRESHOLD_PIXELS } from '@/constants/assistant';
import {
  ASSISTANT_CONVERSATIONS_QUERY_KEY,
  buildAssistantMessagesQueryKey,
} from '@/constants/queryKeys';
import { useAssistantPanel } from '../assistantPanelContext';
import {
  useAssistantVoiceSession,
  type VoiceFailure,
  type VoiceStatus,
} from './useAssistantVoiceSession';
import { visibleCaptions } from './voiceCaptions';

const ACTIVE_STATUSES: VoiceStatus[] = ['listening', 'hearing', 'thinking', 'speaking'];

function isNearBottom(element: HTMLElement): boolean {
  return (
    element.scrollHeight - element.scrollTop - element.clientHeight <=
    ASSISTANT_SCROLL_FOLLOW_THRESHOLD_PIXELS
  );
}

function formatElapsed(totalSeconds: number): { minutes: string; seconds: string } {
  return {
    minutes: String(Math.floor(totalSeconds / 60)),
    seconds: String(totalSeconds % 60).padStart(2, '0'),
  };
}

export function AssistantVoiceView() {
  const { t } = useTranslation(['assistant', 'errors']);
  const { setView, selectConversation } = useAssistantPanel();
  const queryClient = useQueryClient();
  const session = useAssistantVoiceSession(function describeNoteEvent(event) {
    return t('assistant:voice.noteEvents.' + event);
  });
  const { start } = session;
  const captionsContainerRef = useRef<HTMLDivElement | null>(null);
  const isFollowingRef = useRef(true);
  const voiceConversationId = session.conversationId;

  useEffect(
    function showTheVoiceConversationInHistory() {
      if (voiceConversationId !== null) {
        void queryClient.invalidateQueries({ queryKey: ASSISTANT_CONVERSATIONS_QUERY_KEY });
      }
    },
    [queryClient, voiceConversationId]
  );

  function returnToChat() {
    if (voiceConversationId !== null) {
      void queryClient.invalidateQueries({
        queryKey: buildAssistantMessagesQueryKey(voiceConversationId),
      });
      selectConversation(voiceConversationId);
    }
    setView('chat');
  }
  const isActive = ACTIVE_STATUSES.includes(session.status);
  const captions = visibleCaptions(session.captions);

  useEffect(
    function startWhenOpened() {
      void start();
    },
    [start]
  );

  const lastCaption = captions[captions.length - 1];
  const lastCaptionLength = lastCaption === undefined ? 0 : lastCaption.text.length;
  const lastCaptionRole = lastCaption === undefined ? null : lastCaption.role;
  useEffect(
    function followNewestCaption() {
      if (lastCaptionRole === 'user') {
        isFollowingRef.current = true;
      }
      const container = captionsContainerRef.current;
      if (container !== null && isFollowingRef.current) {
        container.scrollTop = container.scrollHeight;
      }
    },
    [captions.length, lastCaptionLength, lastCaptionRole]
  );

  function describeFailure(failure: VoiceFailure): string {
    if (failure.kind === 'microphone') {
      return t('assistant:voice.failures.' + failure.reason);
    }
    if (failure.kind === 'backend') {
      return translateBackendError(t, failure.error);
    }
    return t('assistant:voice.failures.' + failure.kind);
  }

  const isListeningForSpeech = isActive && !session.isPaused && session.status !== 'speaking';
  const statusText =
    isActive && session.isPaused
      ? t('assistant:voice.status.paused')
      : session.status === 'thinking' && session.isRunningTool
        ? t('assistant:voice.status.working')
        : t('assistant:voice.status.' + session.status);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-4 pt-5 pb-3 flex flex-col items-center text-center gap-2 border-b border-zooborder">
        <div
          className={cn(
            'w-16 h-16 rounded-full flex items-center justify-center transition-colors',
            session.status === 'speaking'
              ? 'bg-zooprimary text-zoowhite'
              : session.status === 'hearing'
                ? 'bg-zoovpalelavender text-zooprimary ring-4 ring-zooprimary/30'
                : 'bg-zoovpalelavender text-zooprimary'
          )}
          aria-hidden="true"
        >
          <Icon name={isListeningForSpeech ? 'Mic' : 'MicOff'} className="w-7 h-7" />
        </div>
        <p className="text-sm font-bold text-zooindigo" aria-live="polite">
          {statusText}
        </p>
        {isActive ? (
          <p className="text-xs text-zoomuted tabular-nums">
            {t('assistant:voice.elapsed', formatElapsed(session.elapsedSeconds))}
          </p>
        ) : null}
      </div>

      <div
        ref={captionsContainerRef}
        onScroll={function rememberWhetherFollowing(event) {
          isFollowingRef.current = isNearBottom(event.currentTarget);
        }}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-2"
      >
        {captions.length === 0 && isActive ? (
          <p className="text-sm text-zoomuted">{t('assistant:voice.hint')}</p>
        ) : null}
        {captions.map(function renderCaption(caption) {
          if (caption.role === 'note') {
            return (
              <p
                key={caption.id}
                className="text-xs font-semibold text-zooprimary flex items-center gap-1.5"
              >
                <Icon name="ClipboardList" className="w-3.5 h-3.5" />
                {caption.text}
              </p>
            );
          }
          return (
            <div
              key={caption.id}
              className={cn(
                'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
                caption.role === 'user'
                  ? 'ml-auto bg-zoovpalelavender text-zooindigo'
                  : 'bg-zoooffwhite border border-zooborder text-zooindigo'
              )}
            >
              {caption.text.length > 0 ? caption.text : t('assistant:voice.listeningDots')}
            </div>
          );
        })}
      </div>

      {session.failure !== null ? (
        <p role="alert" className="mx-4 mb-2 text-sm text-zoodangerhover">
          {describeFailure(session.failure)}
        </p>
      ) : null}

      <div className="px-4 py-3 border-t border-zooborder flex items-center gap-2 flex-wrap">
        {isActive || session.status === 'connecting' ? (
          <>
            {session.status === 'speaking' ? (
              <Button size="small" onClick={session.stopSpeaking}>
                <Icon name="CircleStop" className="w-4 h-4" />
                {t('assistant:voice.stopSpeaking')}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="small"
                disabled={session.status === 'connecting'}
                aria-pressed={session.isPaused}
                onClick={session.togglePause}
              >
                <Icon name={session.isPaused ? 'Mic' : 'MicOff'} className="w-4 h-4" />
                {session.isPaused ? t('assistant:voice.resume') : t('assistant:voice.pause')}
              </Button>
            )}
            <Button variant="outline" size="small" onClick={session.stop} className="ml-auto">
              <Icon name="PhoneOff" className="w-4 h-4" />
              {t('assistant:voice.end')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" size="small" onClick={returnToChat}>
              {t('assistant:voice.backToChat')}
            </Button>
            <Button size="small" onClick={() => void start()} className="ml-auto">
              <Icon name="Mic" className="w-4 h-4" />
              {t('assistant:voice.startAgain')}
            </Button>
          </>
        )}
        <p className="basis-full text-[11px] text-zoomuted">{t('assistant:voice.privacy')}</p>
      </div>
    </div>
  );
}
